import React, { useState, useEffect, useRef } from "react";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts";
import PropTypes from "prop-types";
import _ from "lodash";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import ChartStyle from "styles/ChartStyle";
import { chartLineWidth } from "styles/variables";

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    marginTop: 16,
    marginLeft: 16,
    width: "100%",
    display: "block",
  },
  legendSearch: {
    width: 320,
    marginBottom: 8,
  },
}));

const GenericEchart = ({
  legend,
  lblSaveImage = "",
  xAisData,
  seriesData,
  chartType = "line",
  colorConfig = [],
  yAxisConfig,
}) => {
  const classes = useStyles();
  const [seriesType, setSeriesType] = useState(chartType);
  const [chartData, setChartData] = useState([]);
  const [color, setColorArray] = useState([]);
  const [stackState, setStackState] = useState("");
  const [legendQuery, setLegendQuery] = useState("");
  const chartRef = useRef(null);
  const legendClickRef = useRef({ name: "", ts: 0 });

  useEffect(() => {
    echarts.registerTheme("GenericChartTheme", ChartStyle);
  }, []);

  useEffect(() => {
    let seriesDataArray = [];
    let colorArray = colorConfig.length ? colorConfig : [];

    _.forOwn(seriesData, function (value) {
      _.forOwn(value, function (value2, key2) {
        if (key2 !== "date") {
          let eachChartSeries = {};
          eachChartSeries.name = legend[key2] || key2;
          eachChartSeries.type = chartType;
          if (key2.toLowerCase().includes("count")) {
            eachChartSeries.yAxisIndex = 1;
          }
          eachChartSeries.data = value2;
          eachChartSeries.lineStyle = { width: chartLineWidth };
          eachChartSeries.symbolSize =
            seriesData[0]["date"].length > 60 ? 4 : 8;
          eachChartSeries.barGap = 0;
          eachChartSeries.barMaxWidth = 20;
          seriesDataArray.push(eachChartSeries);
        }
      });
    });

    setChartData(seriesDataArray);
    setColorArray(colorArray);
  }, [seriesData, seriesType, legend, colorConfig]);

  const magicTypeChanged = (param) => {
    if (param.currentType === "stack") {
      setStackState("__ec_magicType_stack__");
    } else if (param.currentType === "tiled") {
      setStackState(null);
    } else {
      setSeriesType(param.currentType);
    }
  };

  const normalizedQuery = legendQuery.trim().toLowerCase();
  const visibleLegendNames = Object.values(legend).filter((name) =>
    !normalizedQuery ? true : String(name).toLowerCase().includes(normalizedQuery)
  );
  const visibleSeries = chartData.filter((series) =>
    visibleLegendNames.includes(series.name)
  );

  const onEvents = {
    magictypechanged: magicTypeChanged,
    legendselectchanged: (params) => {
      const now = Date.now();
      const isDoubleClick =
        legendClickRef.current.name === params.name &&
        now - legendClickRef.current.ts < 350;
      legendClickRef.current = { name: params.name, ts: now };

      if (!isDoubleClick || !chartRef.current) return;

      const chartInstance = chartRef.current.getEchartsInstance();
      const legendNames = visibleLegendNames;
      const selectedMap = params.selected || {};
      const selectedCount = legendNames.filter((name) => selectedMap[name]).length;
      const alreadyOnlySelected =
        selectedCount === 1 && !!selectedMap[params.name];

      if (alreadyOnlySelected) {
        chartInstance.dispatchAction({ type: "legendAllSelect" });
        return;
      }

      legendNames.forEach((name) => {
        chartInstance.dispatchAction({
          type: name === params.name ? "legendSelect" : "legendUnSelect",
          name,
        });
      });
    },
  };

  return (
    <div className={classes.chartContainer}>
      <TextField
        className={classes.legendSearch}
        variant="outlined"
        size="small"
        placeholder="検索"
        value={legendQuery}
        onChange={(e) => setLegendQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <ReactEcharts
        ref={chartRef}
        option={{
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "none" },
          },
          grid: {
            left: 64,
            top: 72,
            right: 48,
            bottom: 32,
            containLabel: true,
          },
          legend: {
            show: true,
            type: "scroll",
            left: 64,
            right: 48,
            top: 8,
            itemGap: 12,
            data: visibleLegendNames,
          },
          toolbox: {
            show: false,
            feature: {
              saveAsImage: {
                show: true,
                title: "Save",
                name: lblSaveImage,
                icon: "image://../../static/images/font-icon/save-chart.svg",
              },
            },
          },
          xAxis: {
            type: "category",
            data: xAisData,
          },
          yAxis: yAxisConfig.map(axis => ({
            type: axis.type || "value",
            name: axis.name || "",
            alignTicks: true,
            axisLine: { show: true },
            axisLabel: { formatter: "{value}" },
          })),
          series: visibleSeries,
          color: color,
        }}
        theme="GenericChartTheme"
        notMerge={true}
        lazyUpdate={true}
        onEvents={onEvents}
        style={{ height: "350px", width: "98%" }}
      />
    </div>
  );
};

GenericEchart.propTypes = {
  legend: PropTypes.object.isRequired,
  lblSaveImage: PropTypes.string,
  xAisData: PropTypes.array.isRequired,
  seriesData: PropTypes.array.isRequired,
  chartType: PropTypes.oneOf(["line", "bar"]),
  colorConfig: PropTypes.array,
  yAxisConfig: PropTypes.array,
};

export default GenericEchart;
